// noinspection SpellCheckingInspection

import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Guid from "_shared/guids/guid";
import { BookmarkedIndividuals } from "marketplace/bookmarks/_views/bookmarked-individuals";
import Individual from "marketplace/individuals/individual";
import React from "react";
import { NavigateFunction } from "react-router";
import UserCoworkers from "users/information/coworker-information/_views/user-coworkers";
import UserCoworker from "users/information/coworker-information/user-coworker";
import NetworkedIndividuals from "users/networking/connections/_views/components/networked-individuals";
import Marketplace from "../marketplace/vendor-marketplace";
import UserInformation from "users/information/user-information";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import Company from "marketplace/companies/company";
import CompanyProfile from "marketplace/companies/profiles/company-profile";
import { useSelector } from "react-redux";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";

const PageContainer = styled(Box)(() => ({
  display: "grid",
  gridTemplateRows: "auto 1fr",
  width: "100%",
}));
const TabsContainer = styled("div")(() => ({
  gridRowStart: 1,
}));
const TabContent = styled("div")(() => ({
  gridRowStart: 2,
  gridRowEnd: "none",
}));

type VendorTeamMemberSelectionProps = {
  selectLeader: boolean;
  selectMember: boolean;
  selectedTeamMemberUserIds: Guid[];
  navigate?: NavigateFunction;
  onNewSelection: (teamMember: Individual) => void;
  closeDialog: () => void;
};

export default function VendorTeamMemberSelection(props: Readonly<VendorTeamMemberSelectionProps>) {
  const { selectLeader, selectMember, selectedTeamMemberUserIds, navigate, onNewSelection, closeDialog } = props;

  const userPermissions = useSelector(getSessionPermissions);
  const defaultActiveTab = userPermissions?.includesAccessToMarketplaceSearch ? "marketplace" : "coworkers";

  const [activeTab, setActiveTab] = React.useState(defaultActiveTab);

  function marketplaceIndividualSelected(individual: Individual) {
    if (!individual.profile) throw new Error("Individual profile is required.");
    onNewSelection(individual);
    closeDialog();
  }

  function bookmarkedIndividualSelected(individual: Individual) {
    onNewSelection(individual);
    closeDialog();
  }

  async function networkedIndividualSelected(connection: UserInformation) {
    if (!connection.individualId || !connection.id || !connection.legalEntityId) {
      throw new Error("Networked individual must have an individualId, legalEntityId, and userId");
    }

    // TODO: Pull from Redux store
    const individual = Individual.create({
      id: connection.individualId,
      userId: connection.id,
      profile: IndividualProfile.create({
        firstName: connection.name.firstName ?? "",
        lastName: connection.name.lastName ?? "",
        avatarId: connection.avatarId,
        email: connection.email,
        legalEntityId: connection.legalEntityId,
        userId: connection.id,

      }),
      isVisible: false,
      legalEntityId: connection.legalEntityId,
      company: connection.companyId ? Company.create({
        id: connection.companyId,
        legalEntityId: connection.legalEntityId,
        profile: CompanyProfile.create({
          name: connection.companyName
        }),
        isVisible: connection.companyProfileVisible
      }) : undefined,
    });
    onNewSelection(individual);
    closeDialog();
  }

  async function userCoworkerSelected(coworker: UserCoworker) {
    if (!coworker.individualId || !coworker.userId) throw new Error("Coworker must have an individualId and userId");

    onNewSelection(coworker.asIndividual());
    closeDialog();
  }

  return (
    <PageContainer>
      <TabsContainer>
        <Tabs
          indicatorColor="primary"
          variant="scrollable"
          value={activeTab}
          onChange={(event, tab) => setActiveTab(tab)}
          aria-label="Attorney selection nav"
        >
          {userPermissions?.includesAccessToMarketplaceSearch && (
            <Tab value="marketplace" label="Marketplace" />
          )}
          <Tab value="coworkers" label="Coworkers" />
          <Tab value="network" label="Network" />
          <Tab value="indvBookmarks" label="Bookmarked Individuals" />
        </Tabs>
      </TabsContainer>
      {userPermissions?.includesAccessToMarketplaceSearch && activeTab === "marketplace" && (
        <TabContent>
          <Marketplace
            navigate={navigate}
            selectingLeader={selectLeader}
            selectingMember={selectMember}
            selectedTeamMemberUserIds={selectedTeamMemberUserIds}
            selectingTeam={false}
            onTeamMemberSelected={marketplaceIndividualSelected}
            onTeamSelected={() => {}}
          />
        </TabContent>
      )}
      {activeTab === "coworkers" && (
        <TabContent>
          <UserCoworkers selectedMemberUserIds={selectedTeamMemberUserIds} onSelected={userCoworkerSelected} />
        </TabContent>
      )}
      {activeTab === "network" && (
        <TabContent>
          <NetworkedIndividuals
            selectedMemberUserIds={selectedTeamMemberUserIds}
            onSelected={networkedIndividualSelected}
          />
        </TabContent>
      )}
      {activeTab === "indvBookmarks" && (
        <TabContent>
          <BookmarkedIndividuals
            selectedMemberUserIds={selectedTeamMemberUserIds}
            onSelected={bookmarkedIndividualSelected}
          />
        </TabContent>
      )}
    </PageContainer>
  );
}
